import { FC, useState } from "react";
import { Link } from "react-router-dom";

import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
  CopyIcon,
  ExternalLinkIcon,
} from "lucide-react";

import {
  DetailItem,
  DetailLabel,
  DetailValue,
  FlyoutContainer,
  HeaderContent,
} from "../../../components/Flyouts";
import { monolithMoment } from "../../../utils/date-format";

import CustomAttribute from "../../../types/CustomAttribute";

import { DeleteEvidenceModal, EditEvidenceModal } from "../Modals";
import CopyEvidenceModal from "../Modals/CopyEvidenceModal";
import { Evidence } from "../evidence.types";
import { useEvidenceColumnDefinitions } from "../Hooks";
import { CustomField } from "@/Features/CustomFields/custom-fields.types";
import { EvidenceQuery } from "../evidence.query";
import { useCaseContext } from "@/Features";
import { evidenceDetailPath } from "@/Features/Evidence/evidenceLinks";

type EvidenceFlyoutProps = {
  show: boolean;
  onClose?: () => void;
  defaultInfo?: Evidence;
  query: { evidence_id?: number; uuid?: string };
  onEdit?: () => void;
  onDelete?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onCopy?: () => void;
  onUpdate?: ({
    rowData,
    updateData,
  }: {
    rowData: Evidence;
    updateData: Partial<Evidence>;
  }) => void;
};

type FlyoutDetailsProps = {
  defaultData: Evidence;
  onUpdate?: ({
    rowData,
    updateData,
  }: {
    rowData: Evidence;
    updateData: Partial<Evidence>;
  }) => void;
};

const MainDetails: FC<FlyoutDetailsProps> = ({ defaultData, onUpdate }) => {
  const dataFieldIgnoreList = ["description"];
  const CUSTOM_FIELD_PREFIX = "custom_field_";

  const {
    columnDefinitions,
    customFields,
    isLoading: isColumnDefinitionsLoading,
    isFetched: isColumnDefinitionsFetched,
  } = useEvidenceColumnDefinitions();

  const data = {
    ...defaultData,
    ...(defaultData?.custom_attributes?.reduce(
      (acc: Record<string, any>, attr: CustomAttribute) => {
        acc[`${CUSTOM_FIELD_PREFIX}${attr.field_id}`] = attr.value;
        return acc;
      },
      {}
    ) || {}),
  };

  return (
    <FlyoutContainer>
      <div className="event-details">
        {columnDefinitions
          .filter((col) => !dataFieldIgnoreList.includes(col.dataField))
          .filter((col) => {
            if (col.dataField.includes(CUSTOM_FIELD_PREFIX)) {
              const value = (data as any)[col.dataField];

              // Handle different types of empty values
              if (!value) return false;
              if (Array.isArray(value) && value.length === 0) return false;
              if (typeof value === "string") {
                if (value.trim() === "") return false;
                // Check if it's a JSON string representing an empty array
                try {
                  const parsed = JSON.parse(value);
                  if (Array.isArray(parsed) && parsed.length === 0)
                    return false;
                } catch (e) {
                  // Not JSON, continue with regular string check
                }
              }
              if (typeof value === "object" && Object.keys(value).length === 0)
                return false;

              return true;
            }
            return true;
          })
          .map((col) => {
            const key = col.dataField;
            const label = col.caption;
            let value: React.ReactNode;

            // Check if this is a custom field and handle tagBox specifically
            if (key.includes("custom_field_")) {
              const fieldId = key.replace("custom_field_", "");
              const customAttr = defaultData?.custom_attributes?.find(
                (attr) => attr.field_id === parseInt(fieldId, 10)
              );

              if (customAttr && customFields) {
                const field = customFields.data.find(
                  (f: CustomField) => f.field_id === parseInt(fieldId, 10)
                );
                if (
                  field &&
                  field.editor_type === "tagBox" &&
                  customAttr.value
                ) {
                  try {
                    const parsedValue = JSON.parse(String(customAttr.value));
                    value = Array.isArray(parsedValue)
                      ? parsedValue.join(", ")
                      : String(customAttr.value);
                  } catch (e) {
                    value = String(customAttr.value);
                  }
                } else if (
                  field &&
                  field.editor_type === "dateBox" &&
                  customAttr.value
                ) {
                  value = monolithMoment({
                    timestamp: String(customAttr.value),
                    includeTime: true,
                  });
                } else {
                  value = String(customAttr?.value || "");
                }
              } else {
                value = "";
              }
            } else if (col?.render) {
              value = col.render({
                rowData: defaultData,
                onRowUpdated: ({ rowData, updatedData }) => {
                  onUpdate?.({ rowData, updateData: updatedData });
                },
              });
            } else if (col.dataType === "date") {
              const dateValue = (data as any)[key];
              value =
                dateValue &&
                (typeof dateValue === "string" || typeof dateValue === "number")
                  ? monolithMoment({
                      timestamp: dateValue,
                      includeTime: true,
                    })
                  : dateValue;
            } else {
              value = (data as any)[key];
            }

            return (
              <DetailItem key={key}>
                <DetailLabel>{label}</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailItem>
            );
          })}
        <DetailItem>
          <DetailLabel>Description</DetailLabel>
        </DetailItem>
        <DetailValue>
          {defaultData.description || "No description for this evidence."}
        </DetailValue>
      </div>
    </FlyoutContainer>
  );
};

export const EvidenceFlyout: React.FC<EvidenceFlyoutProps> = ({
  show,
  onClose,
  defaultInfo,
  query,
  onEdit,
  onDelete,
  onNext,
  onPrevious,
  onCopy,
  onUpdate,
}) => {
  const caseContext = useCaseContext();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);

  const _query = {
    ...query,
  };

  const evidenceUpdateMutation = EvidenceQuery.useMutateUpdateEvidence({
    query: _query,
  });

  const { data, isLoading, refetch } = EvidenceQuery.useGetInfiniteEvidence(
    _query,
    {
      initialData: defaultInfo
        ? {
            pages: [
              {
                data: [defaultInfo],
                nextPage: 1,
                page: 1,
                total: 1,
              },
            ],
            pageParams: [1],
          }
        : undefined,
    }
  );

  const records =
    data?.pages?.reduce<Evidence[]>((acc, page) => {
      return [...acc, ...page.data];
    }, []) || [];

  const itemData = records.at(0);

  const handleEditFinished = (e: {
    evidenceInfo: Evidence;
    newAttributes: CustomAttribute[];
    updateData: Partial<Evidence>;
  }) => {
    // Handle custom attributes update properly
    if (e.updateData?.custom_attributes) {
      const oldAttrs = [...(e.evidenceInfo.custom_attributes || [])];
      const newAttrs = [...e.updateData.custom_attributes];

      // Create a map of old attributes for faster lookup
      const oldAttrMap = new Map(oldAttrs.map((attr) => [attr.field_id, attr]));

      // Update or add new attributes
      const updatedAttrs = oldAttrs.map((oldAttr) => {
        const newAttr = newAttrs.find(
          (a: CustomAttribute) => a.field_id === oldAttr.field_id
        );
        if (newAttr) {
          return {
            ...oldAttr,
            value: newAttr.value,
          };
        }
        return oldAttr;
      });

      newAttrs.forEach((newAttr: CustomAttribute) => {
        if (!oldAttrMap.has(newAttr.field_id)) {
          updatedAttrs.push({
            field_id: newAttr.field_id,
            value: newAttr.value,
            attribute_id: 0,
            item_id: e.evidenceInfo.evidence_id,
            field_name: "",
            editor_type: "",
            enabled: 1,
            sort_value: 0,
          } as unknown as CustomAttribute);
        }
      });

      e.updateData.custom_attributes = updatedAttrs;
    }

    refetch();
    onUpdate?.({ rowData: e.evidenceInfo, updateData: e.updateData });
    setShowEditModal(false);
    onEdit?.();
  };
  const handleCopy = () => {
    setShowCopyModal(true);
  };
  const handleDelete = (deletedEvidence: Evidence) => {
    setShowDeleteModal(false);
    onClose?.();
    onDelete?.();
  };
  const handleUpdate = ({
    rowData,
    updateData,
  }: {
    rowData: Evidence;
    updateData: Partial<Evidence>;
  }) => {
    evidenceUpdateMutation.mutate(
      {
        evidence_id: rowData.evidence_id,
        ...updateData,
      },
      {
        onSuccess: () => {
          onUpdate?.({ rowData, updateData });
        },
      }
    );
  };

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{itemData?.evidence_number}</FlyoutTitle>
            <HeaderContent>
              <div>Evidence Details</div>
              {itemData?.uuid && (
                <Link
                  to={evidenceDetailPath(itemData.uuid, {
                    caseId: caseContext?.data?.case_id,
                  })}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    size="xxs"
                    variant="text"
                    leftSection={<ExternalLinkIcon size={14} />}
                    color="primary"
                  >
                    View Evidence
                  </Button>
                </Link>
              )}
              <Button
                size="xxs"
                variant="text"
                leftSection={<CopyIcon size={14} />}
                color="primary"
                onClick={handleCopy}
              >
                Copy
              </Button>
              <Button
                size="xxs"
                variant="text"
                leftSection={<EditIcon size={14} />}
                color="primary"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
              <Button
                size="xxs"
                variant="text"
                leftSection={<TrashIcon size={14} />}
                color="error"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onPrevious?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronLeftIcon size={14} />
                </Button>
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onNext?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronRightIcon size={14} />
                </Button>
              </div>
            </HeaderContent>
          </FlyoutHeader>
          {itemData && (
            <MainDetails defaultData={itemData} onUpdate={handleUpdate} />
          )}
        </>
      )}
      {showEditModal && itemData && (
        <EditEvidenceModal
          open={showEditModal}
          onEditSubmit={handleEditFinished}
          handleClose={() => setShowEditModal(false)}
          defaultEvidenceInfo={itemData}
        />
      )}
      {showCopyModal && itemData && (
        <CopyEvidenceModal
          show={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          onSubmit={() => {
            setShowCopyModal(false);
            onCopy?.();
          }}
          onCancel={() => setShowCopyModal(false)}
          sourceEvidence={itemData}
          includeCaseField={false}
          navigateToItem={true}
        />
      )}
      {showDeleteModal && itemData && (
        <DeleteEvidenceModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          evidenceInfo={itemData}
        />
      )}
    </Flyout>
  );
};
